import React, { useState, useRef, useCallback } from 'react';
import {
  FiCheckCircle, FiCompass, FiCpu, FiFileText, FiGlobe, FiImage, FiInfo, FiLink, FiMap,
  FiSettings, FiType, FiUploadCloud, FiX, FiAlertCircle, FiAlertTriangle
} from 'react-icons/fi';
import '../styles/knowledge-upload.css';
import authService from '../utils/auth.js';
import { getApiBaseUrl } from '../utils/apiBase';

const API_BASE_URL = getApiBaseUrl();

const BACKEND_UNREACHABLE_MSG = 'Backend server is not reachable. Please make sure the NovaBot API server is running.';

function getUploadErrorMessage(error, fallback) {
  if (!error) return fallback;
  const msg = (error?.message || '').toLowerCase();
  if (msg === 'failed to fetch' || error?.name === 'TypeError' || msg.includes('connection reset') || msg.includes('network')) return BACKEND_UNREACHABLE_MSG;
  return error?.message || fallback;
}

const ACCESS_ROLE_OPTIONS = [
  { key: 'business', label: 'Business' },
  { key: 'its', label: 'ITS' },
  { key: 'recruitment', label: 'Recruitment' },
  { key: 'hr', label: 'HR' },
  { key: 'finance', label: 'Finance' },
  { key: 'immigration', label: 'Immigration' }
];

const DOCUMENT_ACCEPT =
  '.pdf,.docx,.xlsx,.xls,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel';

function isKnowledgeDocumentFile(file) {
  if (!file) return false;
  const name = file.name.toLowerCase();
  const type = (file.type || '').toLowerCase();
  return (
    name.endsWith('.pdf') ||
    name.endsWith('.docx') ||
    name.endsWith('.xlsx') ||
    name.endsWith('.xls') ||
    type === 'application/pdf' ||
    type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    type === 'application/vnd.ms-excel' ||
    type === 'application/msword'
  );
}

const UPLOAD_TABS = [
  { id: 'file', label: 'Documents', Icon: FiFileText },
  { id: 'image', label: 'Image (OCR)', Icon: FiImage },
  { id: 'website', label: 'Website', Icon: FiGlobe },
  { id: 'text', label: 'Paste Text', Icon: FiType }
];

const WEBSITE_MODES = [
  { id: 'auto', label: 'Auto', Icon: FiCpu, hint: 'Auto: Detects single page or sitemap automatically' },
  { id: 'single', label: 'Single', Icon: FiFileText, hint: 'Single: Scrapes one page only' },
  { id: 'crawler', label: 'Crawler', Icon: FiLink, hint: 'Crawler: Follows links and crawls multiple pages (no sitemap needed!)' },
  { id: 'sitemap', label: 'Sitemap', Icon: FiMap, hint: 'Sitemap: Crawls all pages from sitemap.xml' }
];

const STATUS_ICONS = { success: FiCheckCircle, error: FiAlertCircle, warning: FiAlertTriangle, info: FiInfo };

function KnowledgeUpload({ onUploadComplete, companyId = null }) {
  const [activeTab, setActiveTab] = useState('file'); // 'file', 'image', 'text', 'website'
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState(null);
  const [textContent, setTextContent] = useState('');
  const [textTitle, setTextTitle] = useState('');
  const [previewImage, setPreviewImage] = useState(null);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [websiteMode, setWebsiteMode] = useState('auto'); // 'auto', 'single', 'sitemap', 'crawler'
  const [crawlerConfig, setCrawlerConfig] = useState({
    maxPages: 50,
    maxDepth: 3,
    pathFilter: '',
    excludePatterns: ''
  });
  const [selectedAccessRoles, setSelectedAccessRoles] = useState([]);
  
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const textAreaRef = useRef(null);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files || []);
    if (!files.length) return;

    if (activeTab === 'file') {
      const documentFiles = files.filter(isKnowledgeDocumentFile);
      if (documentFiles.length) {
        handleFileUpload(documentFiles);
      }
    } else if (activeTab === 'image') {
      const imageFile = files.find((file) => file.type.startsWith('image/'));
      if (imageFile) {
        handleImageUpload(imageFile);
      }
    }
  }, [activeTab]);

  const handleAccessRoleToggle = (roleKey) => {
    setSelectedAccessRoles((prev) => {
      if (prev.includes(roleKey)) {
        return prev.filter((r) => r !== roleKey);
      }
      return [...prev, roleKey];
    });
  };

  const handleFileUpload = async (filesOrSingle) => {
    const files = Array.isArray(filesOrSingle) ? filesOrSingle : [filesOrSingle];
    const documentFiles = files.filter(isKnowledgeDocumentFile);

    if (!documentFiles.length) {
      setStatus({
        type: 'error',
        message: 'Please upload at least one PDF, DOCX, or Excel (.xlsx / .xls) file'
      });
      return;
    }

    setUploading(true);
    setProgress(0);
    setStatus({ type: 'info', message: 'Uploading document(s)...' });

    const formData = new FormData();
    documentFiles.forEach((file) => {
      formData.append('pdf', file);
    });
    if (companyId) {
      formData.append('companyId', companyId);
    }
    if (selectedAccessRoles.length > 0) {
      formData.append('accessRoles', JSON.stringify(selectedAccessRoles));
    }

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 300);

      // Get auth headers and remove Content-Type for FormData (browser will set it with boundary)
      const authHeaders = authService.getAuthHeaders();
      const { 'Content-Type': _, ...headersWithoutContentType } = authHeaders;
      
      const response = await fetch(`${API_BASE_URL}/api/knowledge/upload`, {
        method: 'POST',
        headers: headersWithoutContentType,
        body: formData,
      });

      clearInterval(progressInterval);
      setProgress(100);

      const data = await response.json();

      if (data.success) {
        if (Array.isArray(data.results)) {
          const total = data.results.length;
          const successCount = data.results.filter((r) => r.success).length;
          const failureCount = total - successCount;
          const failureDetails = data.results
            .filter((r) => !r.success)
            .map((r) => `• ${r.fileName}: ${r.error || 'Failed'}`)
            .join('\n');

          let message = `Uploaded ${successCount}/${total} document(s) successfully.`;
          if (failureCount > 0) {
            message += `\n${failureCount} file(s) failed:\n${failureDetails}`;
          }

          setStatus({
            type: failureCount > 0 ? 'warning' : 'success',
            message
          });
        } else {
          setStatus({
            type: 'success',
            message: 'Document uploaded and training completed!'
          });
        }
        setTimeout(() => {
          setStatus(null);
          setProgress(0);
          setUploading(false);
          if (onUploadComplete) onUploadComplete();
        }, 2000);
      } else {
        const failureDetails = Array.isArray(data.results)
          ? data.results
              .filter((r) => !r.success)
              .map((r) => `${r.fileName}: ${r.error || 'Failed'}`)
              .join('\n')
          : '';
        throw new Error(data.error || failureDetails || 'Upload failed');
      }
    } catch (error) {
      setStatus({ type: 'error', message: getUploadErrorMessage(error, 'Failed to upload document') });
      setProgress(0);
      setUploading(false);
    }
  };

  const handleImageUpload = async (file) => {
    if (!file || !file.type.startsWith('image/')) {
      setStatus({ type: 'error', message: 'Please upload an image file' });
      return;
    }

    setUploading(true);
    setProgress(0);
    setStatus({ type: 'info', message: 'Processing image with OCR...' });

    // Preview image
    const reader = new FileReader();
    reader.onload = (e) => setPreviewImage(e.target.result);
    reader.readAsDataURL(file);

    const formData = new FormData();
    formData.append('image', file);
    if (companyId) {
      formData.append('companyId', companyId);
    }
    if (selectedAccessRoles.length > 0) {
      formData.append('accessRoles', JSON.stringify(selectedAccessRoles));
    }

    try {
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 85) {
            clearInterval(progressInterval);
            return 85;
          }
          return prev + 15;
        });
      }, 400);

      // Get auth headers and remove Content-Type for FormData (browser will set it with boundary)
      const authHeaders = authService.getAuthHeaders();
      const { 'Content-Type': _, ...headersWithoutContentType } = authHeaders;
      
      const response = await fetch(`${API_BASE_URL}/api/knowledge/upload-image`, {
        method: 'POST',
        headers: headersWithoutContentType,
        body: formData,
      });

      clearInterval(progressInterval);
      setProgress(100);

      const data = await response.json();

      if (data.success) {
        setStatus({ 
          type: 'success', 
          message: `Image processed! Extracted ${data.chunks} text chunks.` 
        });
        setTimeout(() => {
          setStatus(null);
          setProgress(0);
          setUploading(false);
          setPreviewImage(null);
          if (onUploadComplete) onUploadComplete();
        }, 3000);
      } else {
        throw new Error(data.error || 'Image processing failed');
      }
    } catch (error) {
      setStatus({ type: 'error', message: getUploadErrorMessage(error, 'Failed to process image') });
      setProgress(0);
      setUploading(false);
      setPreviewImage(null);
    }
  };

  const handleTextPaste = async () => {
    if (!textContent.trim() || textContent.trim().length < 10) {
      setStatus({ type: 'error', message: 'Please enter at least 10 characters of text' });
      return;
    }

    setUploading(true);
    setProgress(0);
    setStatus({ type: 'info', message: 'Processing text...' });

    try {
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 20;
        });
      }, 200);

      const response = await fetch(`${API_BASE_URL}/api/knowledge/paste-text`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify({
          text: textContent,
          title: textTitle || `Text Document ${new Date().toLocaleString()}`,
          ...(companyId && { companyId }),
          accessRoles: selectedAccessRoles
        }),
      });

      clearInterval(progressInterval);
      setProgress(100);

      const data = await response.json();

      if (data.success) {
        setStatus({ 
          type: 'success', 
          message: `Text processed! Created ${data.chunks} chunks.` 
        });
        setTimeout(() => {
          setStatus(null);
          setProgress(0);
          setUploading(false);
          setTextContent('');
          setTextTitle('');
          if (onUploadComplete) onUploadComplete();
        }, 2000);
      } else {
        throw new Error(data.error || 'Text processing failed');
      }
    } catch (error) {
      setStatus({ type: 'error', message: getUploadErrorMessage(error, 'Failed to process text') });
      setProgress(0);
      setUploading(false);
    }
  };

  const handleWebsiteScrape = async () => {
    // Validate URL
    if (!websiteUrl.trim()) {
      setStatus({ type: 'error', message: 'Please enter a website URL' });
      return;
    }

    const urlString = websiteUrl.trim();

    // Basic URL validation
    try {
      const url = new URL(urlString);
      if (!['http:', 'https:'].includes(url.protocol)) {
        setStatus({ type: 'error', message: 'Only HTTP and HTTPS URLs are supported' });
        return;
      }
    } catch (error) {
      setStatus({ type: 'error', message: 'Please enter a valid URL (e.g., https://example.com)' });
      return;
    }

    // Auto-detect mode if set to auto
    let actualMode = websiteMode;
    if (websiteMode === 'auto') {
      const isSitemap = urlString.toLowerCase().includes('sitemap') || urlString.toLowerCase().endsWith('.xml');
      actualMode = isSitemap ? 'sitemap' : 'single';
    }

    setUploading(true);
    setProgress(0);
    
    let statusMessage = '';
    switch (actualMode) {
      case 'crawler':
        statusMessage = 'Crawling website and following links...';
        break;
      case 'sitemap':
        statusMessage = 'Crawling sitemap...';
        break;
      case 'single':
      default:
        statusMessage = 'Scraping website...';
    }
    setStatus({ type: 'info', message: statusMessage });

    try {
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 85) {
            clearInterval(progressInterval);
            return 85;
          }
          return prev + (actualMode === 'crawler' || actualMode === 'sitemap' ? 5 : 15);
        });
      }, actualMode === 'crawler' || actualMode === 'sitemap' ? 1500 : 500);

      let response, data;

      if (actualMode === 'crawler') {
        // NEW: Link-following crawler (no sitemap required)
        const excludeArray = crawlerConfig.excludePatterns
          .split(',')
          .map(p => p.trim())
          .filter(Boolean);

        response = await fetch(`${API_BASE_URL}/api/knowledge/crawl-website`, {
          method: 'POST',
          headers: authService.getAuthHeaders(),
          body: JSON.stringify({
            startUrl: urlString,
            maxPages: parseInt(crawlerConfig.maxPages) || 50,
            maxDepth: parseInt(crawlerConfig.maxDepth) || 3,
            sameDomain: true,
            pathFilter: crawlerConfig.pathFilter || null,
            excludePatterns: excludeArray.length > 0 ? excludeArray : undefined,
            ...(companyId && { companyId }),
            accessRoles: selectedAccessRoles
          }),
        });
      } else if (actualMode === 'sitemap') {
        // Sitemap crawling mode
        response = await fetch(`${API_BASE_URL}/api/knowledge/crawl-sitemap`, {
          method: 'POST',
          headers: authService.getAuthHeaders(),
          body: JSON.stringify({
            sitemapUrl: urlString,
            maxPages: 10,
            useJavaScript: false,
            ...(companyId && { companyId }),
            accessRoles: selectedAccessRoles
          }),
        });
      } else {
        // Single page scraping
        response = await fetch(`${API_BASE_URL}/api/knowledge/scrape-website`, {
          method: 'POST',
          headers: authService.getAuthHeaders(),
          body: JSON.stringify({
            url: urlString,
            ...(companyId && { companyId }),
            accessRoles: selectedAccessRoles
          }),
        });
      }

      clearInterval(progressInterval);
      setProgress(100);

      data = await response.json();

      if (data.success) {
        let message;
        if (actualMode === 'crawler') {
          message = `✅ Website crawled! ${data.stats.successful}/${data.stats.totalUrls} pages scraped. Created ${data.stats.totalChunks} chunks.`;
        } else if (actualMode === 'sitemap') {
          message = `✅ Sitemap crawled! ${data.successful || data.stats?.successful || 0}/${data.total || data.stats?.totalUrls || 0} pages scraped.`;
        } else {
          message = `✅ Website scraped! Created ${data.chunks} chunks from "${data.title}".`;
        }
        
        setStatus({ 
          type: 'success', 
          message: message
        });
        
        setTimeout(() => {
          setStatus(null);
          setProgress(0);
          setUploading(false);
          setWebsiteUrl('');
          if (onUploadComplete) onUploadComplete();
        }, 4000);
      } else {
        throw new Error(data.error || 'Website scraping failed');
      }
    } catch (error) {
      setStatus({ type: 'error', message: getUploadErrorMessage(error, 'Failed to scrape website') });
      setProgress(0);
      setUploading(false);
    }
  };


  const StatusIcon = status ? (STATUS_ICONS[status.type] || FiInfo) : null;
  const activeMode = WEBSITE_MODES.find((m) => m.id === websiteMode) || WEBSITE_MODES[0];

  return (
    <div className="knowledge-upload-container">
      <div className="upload-header">
        <span className="upload-header-icon" aria-hidden="true"><FiUploadCloud /></span>
        <div>
          <span className="upload-kicker">Knowledge base</span>
          <h2 className="upload-title">Knowledge Base Upload</h2>
          <p className="upload-subtitle">Upload documents, images, or paste text to train your AI assistant</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="upload-tabs" role="tablist" aria-label="Upload source">
        {UPLOAD_TABS.map(({ id, label, Icon }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={activeTab === id}
            className={`tab-button ${activeTab === id ? 'active' : ''}`}
            onClick={() => setActiveTab(id)}
            disabled={uploading}
          >
            <Icon aria-hidden="true" /> <span>{label}</span>
          </button>
        ))}
      </div>

      {/* Department-level access for RAG filtering */}
      <div className="access-role-section">
        <h3 className="access-role-title">Department Access (RAG Filter)</h3>
        <p className="access-role-subtitle">
          Select which departments should be able to retrieve answers from these documents.
          If none are selected, the content will be treated as shared and accessible to all.
        </p>
        <div className="access-role-checkboxes">
          {ACCESS_ROLE_OPTIONS.map((opt) => (
            <label
              key={opt.key}
              className={`access-role-checkbox ${selectedAccessRoles.includes(opt.key) ? 'is-checked' : ''}`}
            >
              <input
                type="checkbox"
                value={opt.key}
                checked={selectedAccessRoles.includes(opt.key)}
                onChange={() => handleAccessRoleToggle(opt.key)}
                disabled={uploading}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Upload Area */}
      <div className="upload-content" key={activeTab}>
        {activeTab === 'file' && (
          <div
            className={`drop-zone ${isDragging ? 'dragging' : ''} ${uploading ? 'uploading' : ''}`}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={DOCUMENT_ACCEPT}
              multiple
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                if (files.length) {
                  handleFileUpload(files);
                }
              }}
              className="file-input"
              disabled={uploading}
            />
            <div className="drop-zone-content">
              <div className="drop-icon" aria-hidden="true">
                <FiUploadCloud />
              </div>
              <h3 className="drop-title">
                {isDragging ? 'Release to upload' : 'Drop documents here or click to browse'}
              </h3>
              <p className="drop-subtitle">Supports PDF, DOCX, and Excel (.xlsx / .xls) up to 50MB</p>
              <div className="drop-formats" aria-hidden="true">
                <span>PDF</span><span>DOCX</span><span>XLSX</span><span>XLS</span>
              </div>
              <button
                type="button"
                className="browse-button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                Choose File
              </button>
            </div>
          </div>
        )}

        {activeTab === 'image' && (
          <div
            className={`drop-zone ${isDragging ? 'dragging' : ''} ${uploading ? 'uploading' : ''}`}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => e.target.files[0] && handleImageUpload(e.target.files[0])}
              className="file-input"
              disabled={uploading}
            />
            <div className="drop-zone-content">
              {previewImage ? (
                <div className="image-preview">
                  <img src={previewImage} alt="Preview" />
                  <button
                    type="button"
                    className="remove-preview"
                    onClick={() => {
                      setPreviewImage(null);
                      if (imageInputRef.current) imageInputRef.current.value = '';
                    }}
                    aria-label="Remove preview"
                  >
                    <FiX aria-hidden="true" />
                  </button>
                </div>
              ) : (
                <>
                  <div className="drop-icon" aria-hidden="true">
                    <FiImage />
                  </div>
                  <h3 className="drop-title">
                    {isDragging ? 'Release to upload' : 'Drop image here or click to browse'}
                  </h3>
                  <p className="drop-subtitle">Text will be extracted using OCR</p>
                  <button
                    type="button"
                    className="browse-button"
                    onClick={() => imageInputRef.current?.click()}
                    disabled={uploading}
                  >
                    Choose Image
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {activeTab === 'website' && (
          <div className="text-upload-area">
            <div className="text-input-group">
              <label className="text-label" htmlFor="ku-website-url">Website URL</label>
              <div className="ku-url-field">
                <FiGlobe aria-hidden="true" />
                <input
                  id="ku-website-url"
                  type="url"
                  className="text-title-input"
                  placeholder="https://example.com or https://example.com/sitemap.xml"
                  value={websiteUrl}
                  onChange={(e) => setWebsiteUrl(e.target.value)}
                  disabled={uploading}
                />
              </div>
              <small className="ku-hint">
                Paste any website URL or sitemap. Choose a mode below.
              </small>
            </div>

            {/* Scraping Mode Selection */}
            <div className="text-input-group">
              <span className="text-label">Scraping Mode</span>
              <div className="ku-mode-grid" role="radiogroup" aria-label="Scraping mode">
                {WEBSITE_MODES.map(({ id, label, Icon }) => (
                  <button
                    key={id}
                    type="button"
                    role="radio"
                    aria-checked={websiteMode === id}
                    className={`ku-mode-btn ${websiteMode === id ? 'is-active' : ''}`}
                    onClick={() => setWebsiteMode(id)}
                    disabled={uploading}
                  >
                    <Icon aria-hidden="true" /> {label}
                  </button>
                ))}
              </div>
              <small className="ku-hint">{activeMode.hint}</small>
            </div>

            {/* Crawler Advanced Settings */}
            {websiteMode === 'crawler' && (
              <div className="ku-panel">
                <h4 className="ku-panel-title"><FiSettings aria-hidden="true" /> Crawler Settings</h4>
                <div className="ku-field-grid">
                  <div className="ku-field">
                    <label htmlFor="ku-max-pages">Max Pages</label>
                    <input
                      id="ku-max-pages"
                      type="number"
                      min="1"
                      max="100"
                      value={crawlerConfig.maxPages}
                      onChange={(e) => setCrawlerConfig({...crawlerConfig, maxPages: e.target.value})}
                      disabled={uploading}
                    />
                  </div>
                  <div className="ku-field">
                    <label htmlFor="ku-max-depth">Max Depth</label>
                    <input
                      id="ku-max-depth"
                      type="number"
                      min="1"
                      max="5"
                      value={crawlerConfig.maxDepth}
                      onChange={(e) => setCrawlerConfig({...crawlerConfig, maxDepth: e.target.value})}
                      disabled={uploading}
                    />
                  </div>
                </div>
                <div className="ku-field">
                  <label htmlFor="ku-path-filter">Path Filter (optional)</label>
                  <input
                    id="ku-path-filter"
                    type="text"
                    placeholder="/blog/ or /docs/"
                    value={crawlerConfig.pathFilter}
                    onChange={(e) => setCrawlerConfig({...crawlerConfig, pathFilter: e.target.value})}
                    disabled={uploading}
                  />
                  <small>Only crawl URLs containing this path</small>
                </div>
                <div className="ku-field">
                  <label htmlFor="ku-exclude">Exclude Patterns (optional)</label>
                  <input
                    id="ku-exclude"
                    type="text"
                    placeholder="/tag/, /author/, /login"
                    value={crawlerConfig.excludePatterns}
                    onChange={(e) => setCrawlerConfig({...crawlerConfig, excludePatterns: e.target.value})}
                    disabled={uploading}
                  />
                  <small>Skip URLs matching these patterns (comma-separated)</small>
                </div>
              </div>
            )}

            {/* Info box */}
            <div className="ku-info">
              <h4 className="ku-info-title">
                {websiteMode === 'crawler'
                  ? <><FiLink aria-hidden="true" /> Link Crawler</>
                  : <><FiCompass aria-hidden="true" /> Smart Scraping</>}
              </h4>
              <ul className="ku-info-list">
                {websiteMode === 'crawler' ? (
                  <>
                    <li>No sitemap required - automatically discovers links</li>
                    <li>Configurable depth and page limits</li>
                    <li>Optional path filtering (e.g., only /blog/ pages)</li>
                    <li>Handles JavaScript sites automatically</li>
                  </>
                ) : (
                  <>
                    <li>Single page URLs are scraped immediately</li>
                    <li>Sitemap.xml files crawl multiple pages automatically</li>
                    <li>JavaScript sites are handled automatically if needed</li>
                    <li>Works with documentation, blogs, articles, and more</li>
                  </>
                )}
              </ul>
            </div>

            <button
              type="button"
              className="process-text-button"
              onClick={handleWebsiteScrape}
              disabled={uploading || !websiteUrl.trim()}
            >
              <FiGlobe aria-hidden="true" /> {uploading ? 'Scraping Website...' : 'Scrape & Train'}
            </button>
          </div>
        )}

        {activeTab === 'text' && (
          <div className="text-upload-area">
            <div className="text-input-group">
              <label className="text-label" htmlFor="ku-text-title">Document Title (Optional)</label>
              <input
                id="ku-text-title"
                type="text"
                className="text-title-input"
                placeholder="e.g., Company Policy Document"
                value={textTitle}
                onChange={(e) => setTextTitle(e.target.value)}
                disabled={uploading}
              />
            </div>
            <div className="text-input-group">
              <label className="text-label" htmlFor="ku-text-body">Paste or Type Your Text</label>
              <textarea
                id="ku-text-body"
                ref={textAreaRef}
                className="text-area"
                placeholder="Paste your document text here... The AI will learn from this content and be able to answer questions about it."
                value={textContent}
                onChange={(e) => setTextContent(e.target.value)}
                disabled={uploading}
                rows={12}
              />
              <div className="text-char-count">
                {textContent.length} characters
              </div>
            </div>
            <button
              type="button"
              className="process-text-button"
              onClick={handleTextPaste}
              disabled={uploading || textContent.trim().length < 10}
            >
              <FiCpu aria-hidden="true" /> {uploading ? 'Processing...' : 'Process & Train'}
            </button>
          </div>
        )}
      </div>

      {/* Progress Bar */}
      {uploading && (
        <div className="progress-container" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress}>
          <div className="progress-bar">
            <div
              className={`progress-fill ${progress >= 100 ? 'is-done' : ''}`}
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="progress-text">{progress}%</div>
        </div>
      )}

      {/* Status Message */}
      {status && (
        <div className={`status-message ${status.type}`} role={status.type === 'error' ? 'alert' : 'status'}>
          {StatusIcon && <StatusIcon className="status-icon" aria-hidden="true" />}
          <span className="status-text">{status.message}</span>
        </div>
      )}
    </div>
  );
}

export default KnowledgeUpload;

